import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Link from "../audience/ScientificLink";
import { ArrowRight, Check, Search, X } from "lucide-react";
import usePrimaryNavSection from "../../hooks/usePrimaryNavSection";

const DIRECTORY_OFFSET = 180;
const CATEGORY_RAIL_PADDING = 8;

const serviceDisplayTitles = {
  "workflow-assessment": "Automation planning",
  "assay-automation": "Assay automation",
  "cell-based-assay-automation": "Cell-based assays",
  "ipsc-differentiation": "iPSC differentiation",
  "sirna-screening": "siRNA screening",
  "antibody-screening": "Antibody screening",
  "method-development": "Liquid-handler methods",
  "bespoke-assay-development": "Plate assay development",
  "liquid-class-development": "Liquid-class development",
  "method-transfer": "Method transfer",
  "automation-rescue": "Automation recovery",
  "workcell-design-install": "Workcell design & installation",
  "workcell-integration": "Instrument integration",
  "governed-orchestration": "Workflow orchestration",
  "cro-data-harmonization": "CRO data harmonization",
  "scientific-dataset-engineering": "Scientific datasets",
  "private-evaluation-dataset": "Private evaluation datasets",
  "agent-evaluation-harness": "Agent evaluations",
  "model-training-datasets": "Training datasets",
  "agent-runtime-infrastructure": "Agent-to-instrument integration",
  "scientific-ai-guardrails": "Agent permissions & controls",
  "agent-tracing-observability": "Workflow tracing",
  "supervised-agent-orchestration": "Supervised agent workflows",
  "lab-mcp-development": "MCP interfaces",
  "workflow-code-recovery": "Lab software recovery",
  "method-ide": "Method workspace design",
  "automation-sdk": "SDK & API development",
};

export const keepCategoryLinkVisible = (categoryNav, categoryLink) => {
  if (!categoryNav || !categoryLink) return;
  if (categoryNav.scrollWidth <= categoryNav.clientWidth) return;

  const navBounds = categoryNav.getBoundingClientRect();
  const linkBounds = categoryLink.getBoundingClientRect();
  let nextScrollLeft = categoryNav.scrollLeft;

  if (linkBounds.left < navBounds.left + CATEGORY_RAIL_PADDING) {
    nextScrollLeft +=
      linkBounds.left - navBounds.left - CATEGORY_RAIL_PADDING;
  } else if (linkBounds.right > navBounds.right - CATEGORY_RAIL_PADDING) {
    nextScrollLeft +=
      linkBounds.right - navBounds.right + CATEGORY_RAIL_PADDING;
  } else {
    return;
  }

  const maxScrollLeft = Math.max(
    0,
    categoryNav.scrollWidth - categoryNav.clientWidth
  );
  const clampedScrollLeft = Math.min(
    maxScrollLeft,
    Math.max(0, nextScrollLeft)
  );

  if (clampedScrollLeft === categoryNav.scrollLeft) return;

  if (typeof categoryNav.scrollTo === "function") {
    categoryNav.scrollTo({ left: clampedScrollLeft, behavior: "auto" });
    return;
  }

  categoryNav.scrollLeft = clampedScrollLeft;
};

const categoryAnchorId = (categoryId) =>
  ({
    workflow: "solution-outcome-workflow",
    operations: "solution-outcome-operations",
    software: "scientific-software",
    data: "data-ai-solutions",
  })[categoryId];

const primaryNavKeyForCategory = () => "solutions";

const contactHref = (solutionId, source) =>
  `/contact?solution=${solutionId}&source=${source}`;

const solutionMatches = (solution, query) => {
  if (!query) return true;
  const searchable = [
    serviceDisplayTitles[solution.id],
    solution.title,
    solution.summary,
    solution.bestFor,
    solution.group,
    solution.mode,
    ...(solution.deliverables || []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLocaleLowerCase();
  return searchable.includes(query);
};

const solutionRowsFor = (categorySolutions) => {
  const rows = [];
  let currentGroup = null;

  categorySolutions.forEach((solution) => {
    if (solution.group && solution.group !== currentGroup) {
      currentGroup = solution.group;
      rows.push({
        type: "group",
        label: solution.group,
        key: `group-${solution.group}`,
      });
    }
    rows.push({ type: "solution", solution, key: solution.id });
  });

  return rows;
};

const SolutionDetailDialog = ({ solution, onClose }) => {
  const dialogRef = useRef(null);
  const titleRef = useRef(null);

  useEffect(() => {
    if (!solution) return undefined;

    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    const previousFocus = document.activeElement;
    const previousBodyStyles = {
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
      overflow: document.body.style.overflow,
    };
    const savedScrollY = window.scrollY;

    document.body.style.position = "fixed";
    document.body.style.top = `-${savedScrollY}px`;
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";

    if (typeof dialog.showModal === "function" && !dialog.open) {
      dialog.showModal();
    } else if (!dialog.open) {
      dialog.setAttribute("open", "");
    }

    titleRef.current?.focus();

    const handleCancel = (event) => {
      event.preventDefault();
      onClose();
    };
    dialog.addEventListener("cancel", handleCancel);

    return () => {
      dialog.removeEventListener("cancel", handleCancel);
      if (dialog.open && typeof dialog.close === "function") dialog.close();
      else dialog.removeAttribute("open");

      document.body.style.position = previousBodyStyles.position;
      document.body.style.top = previousBodyStyles.top;
      document.body.style.width = previousBodyStyles.width;
      document.body.style.overflow = previousBodyStyles.overflow;
      window.scrollTo(0, savedScrollY);

      window.requestAnimationFrame(() => {
        const isVisible = (element) =>
          element instanceof HTMLElement &&
          element.isConnected &&
          !element.closest("[hidden]") &&
          element.getClientRects().length > 0;
        if (previousFocus !== document.body && isVisible(previousFocus)) {
          previousFocus.focus();
          return;
        }
        const trigger = document.getElementById(solution.id);
        const search = document.getElementById("solutions-search");
        if (isVisible(trigger)) trigger.focus();
        else if (isVisible(search)) search.focus();
        else document.getElementById("solution-directory-toggle")?.focus();
      });
    };
  }, [onClose, solution]);

  if (!solution) return null;

  const SolutionIcon = solution.icon;
  const titleId = `solution-detail-title-${solution.id}`;
  const summaryId = `solution-detail-summary-${solution.id}`;

  return (
    <dialog
      ref={dialogRef}
      id="solution-detail-dialog"
      aria-labelledby={titleId}
      aria-describedby={summaryId}
      className="solution-detail-dialog fixed inset-y-0 left-auto right-0 m-0 h-[100dvh] max-h-none w-full max-w-xl overflow-hidden border-0 bg-white p-0 text-slate-950 shadow-2xl outline-none dark:bg-[#090b11] dark:text-white"
    >
      <div className="flex min-h-[72px] shrink-0 items-center justify-end border-b border-slate-200 bg-white/95 px-5 backdrop-blur-xl dark:border-white/[0.1] dark:bg-[#090b11]/95 sm:px-7">
        <button
          type="button"
          onClick={onClose}
          className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-lg text-slate-500 outline-none transition-colors duration-200 hover:bg-slate-100 hover:text-slate-950 focus-visible:ring-2 focus-visible:ring-primary dark:text-white/[0.60] dark:hover:bg-white/[0.07] dark:hover:text-white"
          aria-label="Close solution details"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <article
        id={`solution-detail-${solution.id}`}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-8 sm:px-9 sm:py-10"
      >
        <div className="mb-8">
          <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-primary dark:border-white/[0.1] dark:bg-white/[0.04]">
            <SolutionIcon className="h-5 w-5" aria-hidden="true" />
          </span>
        </div>

        <h3
          ref={titleRef}
          id={titleId}
          tabIndex={-1}
          className="mb-4 text-3xl font-semibold leading-tight tracking-[-0.025em] outline-none sm:text-4xl"
        >
          {solution.title}
        </h3>
        {solution.availability && solution.availability !== "Available" && (
          <p className="mb-4 text-sm text-slate-500 dark:text-white/[0.55]">
            {solution.availability}
          </p>
        )}
        <p
          id={summaryId}
          className="mb-8 text-base leading-7 text-slate-600 dark:text-white/[0.68]"
        >
          {solution.summary}
        </p>

        <div className="mb-8 border-y border-slate-200 py-5 dark:border-white/[0.1]">
          <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-white/[0.55]">
            Best for
          </p>
          <p className="text-base leading-7 text-slate-700 dark:text-white/[0.72]">
            {solution.bestFor}
          </p>
        </div>

        {solution.boundary && (
          <div className="mb-8 border-l-2 border-primary bg-primary/[0.04] px-4 py-4 dark:bg-primary/[0.07]">
            <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-primary-600 dark:text-primary-400">
              Scope boundary
            </p>
            <p className="text-sm leading-6 text-slate-600 dark:text-white/[0.68]">
              {solution.boundary}
            </p>
          </div>
        )}

        <p className="mb-4 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-white/[0.55]">
          What you receive
        </p>
        <ul className="mb-9 space-y-3">
          {solution.deliverables.map((deliverable) => (
            <li
              key={deliverable}
              className="flex items-start gap-3 text-sm leading-6 text-slate-700 dark:text-white/[0.72]"
            >
              <Check className="mt-1 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <span>{deliverable}</span>
            </li>
          ))}
        </ul>

      </article>

      <div className="shrink-0 border-t border-slate-200 bg-white/95 px-6 py-4 backdrop-blur-xl dark:border-white/[0.1] dark:bg-[#090b11]/95 sm:px-9">
        <Link
          to={contactHref(solution.id, `solutions-${solution.id}`)}
          data-cta={solution.id}
          data-solution-id={solution.id}
          className="group inline-flex min-h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-primary-600 px-5 py-3 text-sm font-semibold text-white outline-none transition-colors duration-200 hover:bg-primary-700 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#090b11] sm:w-auto"
        >
          {solution.cta}
          <ArrowRight
            className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5"
            aria-hidden="true"
          />
        </Link>
      </div>
    </dialog>
  );
};

const SolutionDirectory = ({ categories, solutions, expanded = true }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const categoryNavRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const deferredQuery = useDeferredValue(searchQuery.trim().toLocaleLowerCase());

  useEffect(() => {
    const anchor = location.hash.slice(1);
    const isDirectoryAnchor =
      anchor === "solutions-directory-list" ||
      categories.some(({ id }) => categoryAnchorId(id) === anchor) ||
      solutions.some(({ id }) => id === anchor);

    if (isDirectoryAnchor) setSearchQuery("");
  }, [categories, location.hash, solutions]);

  const groupedCategories = useMemo(
    () =>
      categories.map((category) => ({
        ...category,
        solutions: solutions.filter((solution) => solution.category === category.id),
      })),
    [categories, solutions]
  );

  const filteredCategories = useMemo(
    () =>
      groupedCategories
        .map((category) => ({
          ...category,
          solutions: category.solutions.filter((solution) =>
            solutionMatches(solution, deferredQuery)
          ),
        }))
        .filter((category) => category.solutions.length > 0),
    [deferredQuery, groupedCategories]
  );

  const resultCount = filteredCategories.reduce(
    (total, category) => total + category.solutions.length,
    0
  );
  const params = new URLSearchParams(location.search);
  const activeSolutionId = params.get("service");
  const activeSolution = solutions.find(({ id }) => id === activeSolutionId) || null;

  const activeFamily = usePrimaryNavSection({
    enabled: expanded && !activeSolution,
    defaultKey: "workflow",
    selector: "[data-solution-family]",
    dataKey: "solutionFamily",
    activationOffset: DIRECTORY_OFFSET + 10,
  });
  const displayedActiveFamily =
    deferredQuery && filteredCategories.length === 1
      ? filteredCategories[0].id
      : activeFamily;

  useEffect(() => {
    const categoryNav = categoryNavRef.current;
    const activeLink = categoryNav?.querySelector(
      `[data-solution-family-link="${displayedActiveFamily}"]`
    );
    keepCategoryLinkVisible(categoryNav, activeLink);
  }, [displayedActiveFamily]);

  useEffect(() => {
    if (!expanded || !deferredQuery) return undefined;

    const frameId = window.requestAnimationFrame(() => {
      const results = document.getElementById("solutions-directory-list");
      if (!results) return;

      const top =
        window.scrollY +
        results.getBoundingClientRect().top -
        DIRECTORY_OFFSET;
      window.scrollTo({ top: Math.max(0, top), behavior: "auto" });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [deferredQuery, expanded, resultCount]);

  const openSolution = (solutionId) => {
    const nextParams = new URLSearchParams(location.search);
    nextParams.set("service", solutionId);
    navigate(
      {
        pathname: location.pathname,
        search: `?${nextParams.toString()}`,
        hash: location.hash,
      },
      {
        state: {
          ...location.state,
          preserveScroll: true,
          solutionsDetailEntry: true,
        },
      }
    );
  };

  const closeSolution = useCallback(() => {
    if (location.state?.solutionsDetailEntry) {
      navigate(-1);
      return;
    }

    const nextParams = new URLSearchParams(location.search);
    nextParams.delete("service");
    const nextSearch = nextParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
        hash: location.hash,
      },
      { replace: true, state: { preserveScroll: true } }
    );
  }, [location.hash, location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    if (!activeSolutionId || activeSolution) return;
    const nextParams = new URLSearchParams(location.search);
    nextParams.delete("service");
    const nextSearch = nextParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
        hash: location.hash,
      },
      { replace: true, state: { preserveScroll: true } }
    );
  }, [activeSolution, activeSolutionId, location.hash, location.pathname, location.search, navigate]);

  const clearSearch = () => setSearchQuery("");

  return (
    <>
      <div id="solution-directory-content" hidden={!expanded}>
      <div className="solution-directory-toolbar sticky top-[72px] z-40 -mx-6 mb-8 border-y border-slate-200 bg-white px-6 py-3 dark:border-white/[0.1] dark:bg-[#050507]">
        <div className="wide-shell-lg mx-auto flex max-w-6xl flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full lg:max-w-[21rem]">
            <label htmlFor="solutions-search" className="sr-only">
              Search solutions
            </label>
            <Search
              className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-white/[0.40]"
              aria-hidden="true"
            />
            <input
              id="solutions-search"
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape" && searchQuery) {
                  event.preventDefault();
                  clearSearch();
                }
              }}
              aria-controls="solutions-directory-list"
              placeholder="Search by problem or service"
              className="h-12 w-full rounded-lg border border-slate-300 bg-white pl-11 pr-12 text-base text-slate-950 outline-none transition-colors duration-200 placeholder:text-slate-400 focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-white/[0.14] dark:bg-transparent dark:text-white dark:placeholder:text-white/35"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-1 top-1/2 flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-lg text-slate-400 outline-none transition-colors hover:bg-slate-100 hover:text-slate-950 focus-visible:ring-2 focus-visible:ring-primary dark:text-white/[0.45] dark:hover:bg-white/[0.07] dark:hover:text-white"
                aria-label="Clear solution search"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </div>

          <nav
            ref={categoryNavRef}
            aria-label="Solution categories"
            className="-mx-1 flex max-w-full items-center gap-1 overflow-x-auto px-1 pb-1 lg:mx-0 lg:pb-0"
          >
            {groupedCategories.map((category) => {
              const isCurrent = category.id === displayedActiveFamily;
              return (
                <Link
                  key={category.id}
                  data-solution-family-link={category.id}
                  to={{
                    pathname: location.pathname,
                    search: location.search,
                    hash: `#${categoryAnchorId(category.id)}`,
                  }}
                  state={{ preserveScroll: false }}
                  onClick={clearSearch}
                  aria-current={isCurrent ? "location" : undefined}
                  className={`inline-flex min-h-11 shrink-0 cursor-pointer items-center border-b px-3 text-sm font-medium outline-none transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-primary ${
                    isCurrent
                      ? "border-primary text-primary-700 dark:text-primary-400"
                      : "border-transparent text-slate-600 hover:border-slate-300 hover:text-slate-950 dark:text-white/[0.58] dark:hover:border-white/[0.14] dark:hover:text-white"
                  }`}
                >
                  {category.shortLabel}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      <div id="solutions-directory-list" className="border-t border-slate-200 dark:border-white/[0.1]">
        {filteredCategories.map((category) => {
          const rows = solutionRowsFor(category.solutions);

          return (
            <section
              key={category.id}
              id={categoryAnchorId(category.id)}
              data-primary-nav={primaryNavKeyForCategory(category.id)}
              data-solution-family={category.id}
              style={{ scrollMarginTop: `${DIRECTORY_OFFSET}px` }}
              aria-labelledby={`solution-family-title-${category.id}`}
              className="solution-directory-category grid gap-x-12 border-b border-slate-200 dark:border-white/[0.1] lg:grid-cols-[minmax(14rem,0.32fr)_minmax(0,0.68fr)]"
            >
              <div className="solution-directory-category-copy border-b border-slate-200 py-7 dark:border-white/[0.1] lg:border-b-0 lg:py-8">
                <h3
                  id={`solution-family-title-${category.id}`}
                  className="mb-3 font-serif text-3xl font-normal leading-tight tracking-[-0.025em] [text-wrap:pretty]"
                >
                  {category.label}
                </h3>
                <p className="max-w-sm text-sm leading-6 text-slate-600 dark:text-white/[0.62]">
                  {category.description}
                </p>
              </div>

              <ul aria-label={`${category.label} services`}>
                {rows.map((row) => {
                  if (row.type === "group") {
                    return (
                      <li
                        key={row.key}
                        className="px-1 pb-2 pt-6"
                      >
                        <h4 className="text-xs font-medium text-slate-500 dark:text-white/[0.45]">
                          {row.label}
                        </h4>
                      </li>
                    );
                  }

                  const { solution } = row;
                  const displayTitle = serviceDisplayTitles[solution.id] || solution.title;
                  return (
                    <li key={solution.id} className="border-b border-slate-200 last:border-b-0 dark:border-white/[0.08]">
                      <button
                        id={solution.id}
                        type="button"
                        data-solution-row={solution.id}
                        aria-label={displayTitle === solution.title ? displayTitle : `${displayTitle}: ${solution.title}`}
                        aria-haspopup="dialog"
                        aria-controls="solution-detail-dialog"
                        onClick={() => openSolution(solution.id)}
                        style={{ scrollMarginTop: `${DIRECTORY_OFFSET}px` }}
                        className="solution-directory-row group flex min-h-[64px] w-full cursor-pointer items-center gap-4 px-1 py-4 text-left outline-none transition-colors duration-200 hover:text-primary-700 focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary dark:hover:text-primary-400"
                      >
                        <span className="min-w-0 flex-1 text-sm font-medium leading-6 tracking-[-0.01em] sm:text-base">
                          {displayTitle}
                        </span>
                        <ArrowRight
                          className="h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-primary dark:text-white/[0.38]"
                          aria-hidden="true"
                        />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}

        {resultCount === 0 && (
          <div className="border-b border-slate-200 py-16 text-center dark:border-white/[0.1]">
            <p className="text-lg font-semibold text-slate-950 dark:text-white">
              No solutions match “{searchQuery}”.
            </p>
            <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-600 dark:text-white/[0.60]">
              Try a service name, instrument problem, data workflow, or scientific AI use case.
            </p>
            <button
              type="button"
              onClick={clearSearch}
              className="mt-6 inline-flex min-h-11 cursor-pointer items-center justify-center rounded-lg border border-slate-300 px-4 text-sm font-semibold text-slate-700 outline-none transition-colors hover:border-primary/40 hover:text-primary-700 focus-visible:ring-2 focus-visible:ring-primary dark:border-white/[0.16] dark:text-white/[0.70] dark:hover:text-white"
            >
              Clear search
            </button>
          </div>
        )}
      </div>

      <p role="status" className="sr-only" aria-live="polite">
        Showing {resultCount} {resultCount === 1 ? "service" : "services"}.
      </p>
      </div>

      <SolutionDetailDialog solution={activeSolution} onClose={closeSolution} />
    </>
  );
};

export default SolutionDirectory;
